"use client";

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import {
  deleteChatMessage,
  postChatMessageWithAttachments,
  type ChatMessage,
  type ChatMessageAttachment,
} from "@/lib/api";
import { useTeamChat } from "./TeamChatContext";
import type { RealtimeStatus } from "@/hooks/useChatRealtime";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";
const ENTITY_TYPE = "team";
const ENTITY_ID = "general";

const MAX_FILES = 10;
const MAX_FILE_SIZE = 20 * 1024 * 1024;

const ROLE_LABELS: Record<string, string> = {
  admin: "관리자",
  inventory: "재고",
  as: "AS",
  store: "영업",
  repair: "수리",
  viewer: "조회",
};

// ─── 직원별 고정 색상 팔레트 ─────────────────────────────────────
// Tailwind JIT 가 stripping 하지 않도록 클래스 문자열 전체를 정적으로 박아둔다.
// user_id (있으면) 우선, 없으면 sender_name 해시 — 같은 직원은 새로고침/순서/날짜
// 무관하게 동일 색상이 보장된다.
const SENDER_PALETTE = [
  { avatar: "bg-blue-500 text-white",    name: "text-blue-700",    accent: "border-blue-400" },
  { avatar: "bg-emerald-500 text-white", name: "text-emerald-700", accent: "border-emerald-400" },
  { avatar: "bg-violet-500 text-white",  name: "text-violet-700",  accent: "border-violet-400" },
  { avatar: "bg-amber-500 text-white",   name: "text-amber-700",   accent: "border-amber-400" },
  { avatar: "bg-rose-500 text-white",    name: "text-rose-700",    accent: "border-rose-400" },
  { avatar: "bg-cyan-500 text-white",    name: "text-cyan-700",    accent: "border-cyan-400" },
  { avatar: "bg-lime-600 text-white",    name: "text-lime-700",    accent: "border-lime-500" },
  { avatar: "bg-fuchsia-500 text-white", name: "text-fuchsia-700", accent: "border-fuchsia-400" },
] as const;

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function getSenderColor(
  userId: number | null | undefined,
  name: string,
): (typeof SENDER_PALETTE)[number] {
  const key = userId != null ? userId : hashString(name || "");
  return SENDER_PALETTE[key % SENDER_PALETTE.length];
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const isToday =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  if (isToday) {
    return d.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" });
  }
  return (
    d.toLocaleDateString("ko-KR", { month: "short", day: "numeric" }) +
    " " +
    d.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function authHeaders(): HeadersInit {
  if (typeof window === "undefined") return {};
  const token = localStorage.getItem("access_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// 결과 맵: 키 없음 = 로딩 중, null = 로드 실패, string = blob URL 로드 완료.
function useAuthorizedImage(
  attachments: ChatMessageAttachment[],
): Map<number, string | null> {
  const [urlMap, setUrlMap] = useState<Map<number, string | null>>(new Map());
  const cacheRef = useRef<Map<number, string | null>>(new Map());

  useEffect(() => {
    const targets = attachments.filter(
      (a) => a.kind === "image" && !cacheRef.current.has(a.id),
    );
    if (targets.length === 0) return;

    let cancelled = false;
    (async () => {
      const next = new Map(cacheRef.current);
      for (const a of targets) {
        try {
          const res = await fetch(`${API_URL}${a.thumbnail_url}`, {
            headers: authHeaders(),
          });
          if (!res.ok) {
            // 실패도 명시적으로 기록 — 무한 "로드 중…" 방지.
            next.set(a.id, null);
            continue;
          }
          const blob = await res.blob();
          next.set(a.id, URL.createObjectURL(blob));
        } catch {
          next.set(a.id, null);
        }
      }
      if (cancelled) return;
      cacheRef.current = next;
      setUrlMap(new Map(next));
    })();

    return () => {
      cancelled = true;
    };
  }, [attachments]);

  useEffect(() => {
    return () => {
      cacheRef.current.forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
      cacheRef.current = new Map();
    };
  }, []);

  return urlMap;
}

async function downloadAttachment(att: ChatMessageAttachment) {
  try {
    const res = await fetch(`${API_URL}${att.download_url}`, {
      headers: authHeaders(),
    });
    if (!res.ok) {
      alert("다운로드 실패");
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = att.filename;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    alert("다운로드 실패");
  }
}

interface ImagePreview {
  url: string;
  filename: string;
}

export interface TeamChatViewProps {
  mode: "widget" | "page";
  /** 전체화면 확장 버튼 (widget 모드에서만 의미) */
  onExpandFullScreen?: () => void;
  /** 닫기 (widget 모드에서만 의미) */
  onClose?: () => void;
  /** 상태 라벨 표시용 */
  realtimeStatus?: RealtimeStatus;
  // [ERP-FLOATING-CHAT-MEMO-POSITION-PER-DEVICE-DEV-20260629-001]
  // widget 헤더 우측에 주입할 추가 요소(위치 변경 ⚙️ 메뉴 등). widget 모드에서만 렌더.
  headerExtra?: ReactNode;
}

export default function TeamChatView({
  mode,
  onExpandFullScreen,
  onClose,
  realtimeStatus,
  headerExtra,
}: TeamChatViewProps) {
  const {
    messages,
    addMessage,
    currentUser,
    markAllRead,
    hasMoreOlder,
    loadOlder,
    loadingOlder,
    soundEnabled,
    toggleSound,
    applyServerMessages,
  } = useTeamChat();

  // [ERP-TEAMCHAT-READ-RECEIPTS-AND-SOFT-DELETE-DEV-20260703-031]
  // 본인 메시지 soft delete — confirm 후 API 호출, 반환된 masked 메시지로 즉시 반영.
  async function handleDeleteMessage(msg: ChatMessage) {
    if (!window.confirm("이 메시지를 삭제할까요?")) return;
    try {
      const updated = await deleteChatMessage(msg.id);
      applyServerMessages([updated]);
    } catch (e) {
      window.alert(`삭제 실패: ${e instanceof Error ? e.message : e}`);
    }
  }

  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [text, setText] = useState("");
  // 큐 길이 — 버튼 라벨 표시용. 가드 아님(전송 차단 X).
  const [queueLen, setQueueLen] = useState(0);
  const [imagePreview, setImagePreview] = useState<ImagePreview | null>(null);
  const [dragActive, setDragActive] = useState(false);

  // [ERP-TEAMCHAT-ENTER-BEHAVIOR-PC-MOBILE-SPLIT-DEV-20260702-025]
  // PC(>=769px): Enter 전송 / Shift+Enter 줄바꿈. 모바일(<=768px): Enter 줄바꿈, 전송은 버튼만.
  // SSR 안전: 초기 false → mount 후 matchMedia 로 판별 + viewport 변경 구독.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const [currentRole, setCurrentRole] = useState<string | undefined>(undefined);

  // [ERP-TEAM-CHAT-GLOBAL-SOUND-NOTIFICATION-FIX-20260528-001]
  // 알림음 감지/재생 로직은 TeamChatProvider 로 이동. TeamChatView 는 토글 버튼 UI 만 담당.
  // (TeamChatView 가 unmount 되어도 Provider 는 layout.tsx 에서 항상 mount 이므로
  // 채팅창 닫힘 / 다른 페이지 상태에서도 정상 동작.)

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const stickToBottomRef = useRef<boolean>(true);

  // ── 전송 큐 ─────────────────────────────────────────────
  // Enter/버튼 클릭 시 enqueue 만 동기로 즉시 처리 → input 즉시 clear.
  // flushQueue 가 단일 worker 로 순차 POST. 빠른 연속 입력 시
  // React state batching/IME/setText timing 에 영향받지 않음.
  type QueuedMessage = {
    message: string;
    files: File[];
    sender_name: string;
    sender_role?: string;
  };
  const sendQueueRef = useRef<QueuedMessage[]>([]);
  const flushingRef = useRef<boolean>(false);
  // addMessage 는 stable callback 이지만, flush 루프가 오래 돌 수 있어 ref 로 고정.
  const addMessageRef = useRef(addMessage);
  useEffect(() => {
    addMessageRef.current = addMessage;
  }, [addMessage]);

  useEffect(() => {
    setCurrentRole(
      typeof window !== "undefined"
        ? localStorage.getItem("user_role") || undefined
        : undefined,
    );
  }, []);

  // 자동 스크롤 — [ERP-TEAMCHAT-READ-RECEIPTS-AND-INSTANT-LATEST-SCROLL-DEV-20260703-029]
  // 애니메이션 없이 즉시 하단 이동. 최초 로드/리로드/재진입 시 top→bottom 으로 주욱 내려오던
  // smooth scrollIntoView 제거 → 스크롤 컨테이너 scrollTop 을 즉시 최신으로 설정.
  //   · 최초(마운트 후 첫 메시지 렌더): 무조건 즉시 하단(useLayoutEffect 로 paint 전 점프 → 깜빡임 방지)
  //   · 이후 갱신: 사용자가 하단 근처(stickToBottom)일 때만 즉시 하단 유지(과거 보는 중엔 강제 안 함)
  // loadOlder prepend 위치 보정 effect 는 그대로 유지(사용자가 위로 스크롤 시 stickToBottom=false 라 여기서 skip).
  const didInitialScrollRef = useRef(false);
  useLayoutEffect(() => {
    if (messages.length === 0) return;
    const el = messagesScrollRef.current;
    if (!el) return;
    if (!didInitialScrollRef.current) {
      didInitialScrollRef.current = true;
      el.scrollTop = el.scrollHeight; // 최초: 즉시 최신
      return;
    }
    if (!stickToBottomRef.current) return;
    el.scrollTop = el.scrollHeight; // 하단 유지도 즉시(smooth 아님)
  }, [messages]);

  // ── 26차 후속 #61: 더 오래된 페이지 prepend 시 스크롤 위치 보정 ───
  // loadOlder() 가 messages 앞쪽에 N건을 prepend 하면 scrollTop 은 그대로지만
  // 사용자가 보던 메시지가 화면에서 위로 밀려 올라간다. prepend 직전의
  // scrollHeight 를 캡처해두고, 다음 렌더 직후 scrollTop += (newHeight - oldHeight)
  // 로 보정해 시각적으로 같은 위치 유지.
  const pendingScrollFixRef = useRef<number | null>(null);

  async function handleLoadOlder() {
    const el = messagesScrollRef.current;
    if (!el) return;
    pendingScrollFixRef.current = el.scrollHeight;
    await loadOlder();
  }

  useEffect(() => {
    if (pendingScrollFixRef.current === null) return;
    const el = messagesScrollRef.current;
    if (!el) {
      pendingScrollFixRef.current = null;
      return;
    }
    const delta = el.scrollHeight - pendingScrollFixRef.current;
    pendingScrollFixRef.current = null;
    if (delta > 0) {
      // 자동 스크롤(stickToBottom) 무력화 — prepend 동안 bottom으로 끌려가지 않게.
      stickToBottomRef.current = false;
      el.scrollTop = el.scrollTop + delta;
    }
  }, [messages]);

  // 뷰가 보이는 동안은 자동으로 읽음 처리
  useEffect(() => {
    if (messages.length === 0) return;
    markAllRead();
  }, [messages, markAllRead]);

  function onScroll() {
    const el = messagesScrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = distanceFromBottom < 80;
  }

  function appendFiles(incoming: FileList | File[]) {
    const arr = Array.from(incoming);
    const next: File[] = [];
    for (const f of arr) {
      if (f.size > MAX_FILE_SIZE) {
        alert(`파일이 너무 큽니다 (최대 20MB): ${f.name}`);
        continue;
      }
      next.push(f);
    }
    setPendingFiles((prev) => {
      const combined = [...prev, ...next];
      if (combined.length > MAX_FILES) {
        alert(`파일은 최대 ${MAX_FILES}개까지 첨부 가능합니다`);
        return combined.slice(0, MAX_FILES);
      }
      return combined;
    });
  }

  function removePending(idx: number) {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  }

  // 클립보드 이미지 붙여넣기 — 입력 영역 포커스 중일 때만
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function onPaste(e: ClipboardEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(document.activeElement)) return;
      if (!e.clipboardData) return;
      const files: File[] = [];
      for (const item of Array.from(e.clipboardData.items)) {
        if (item.kind === "file") {
          const f = item.getAsFile();
          if (f) files.push(f);
        }
      }
      if (files.length > 0) appendFiles(files);
    }
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, []);

  async function flushQueue() {
    if (flushingRef.current) return;
    flushingRef.current = true;
    try {
      // shift 로 하나씩 꺼내며 순차 POST. 큐가 빌 때까지 반복 — 도중에 enqueue 된
      // 새 항목도 같은 worker 가 처리하므로 추가 동시성 없음.
      while (sendQueueRef.current.length > 0) {
        const item = sendQueueRef.current.shift()!;
        setQueueLen(sendQueueRef.current.length + 1); // 현재 처리 중 1 + 대기
        try {
          const msg = await postChatMessageWithAttachments({
            entity_type: ENTITY_TYPE,
            entity_id: ENTITY_ID,
            sender_name: item.sender_name,
            sender_role: item.sender_role,
            message: item.message,
            files: item.files,
          });
          // WS broadcast 와 id 중복은 Context 의 mergeById 가 처리
          addMessageRef.current(msg);
        } catch (e) {
          // 실패한 항목은 drop (큐가 막히지 않게) + 사용자에게 알림.
          alert(
            (e instanceof Error ? e.message : "전송 실패") +
              ` — "${item.message.slice(0, 30)}"`,
          );
        } finally {
          setQueueLen(sendQueueRef.current.length);
        }
      }
    } finally {
      flushingRef.current = false;
      setQueueLen(0);
    }
  }

  function enqueueMessage(message: string, files: File[]) {
    sendQueueRef.current.push({
      message,
      files,
      sender_name: currentUser,
      sender_role: currentRole,
    });
    setQueueLen(sendQueueRef.current.length);
    // fire-and-forget — flushQueue 는 자체 reentry 가드 보유
    void flushQueue();
  }

  function send(rawText?: string) {
    // 1) 입력 캡처 — Enter 시 textarea native value 우선, 아니면 controlled state.
    //    어느 경로든 동기 캡처라 React batching 영향 없음.
    const source =
      rawText !== undefined ? rawText : textareaRef.current?.value ?? text;
    const trimmed = source.trim();
    const filesSnapshot = pendingFiles;
    if (!trimmed && filesSnapshot.length === 0) return;

    // 2) 즉시 enqueue + clear — await 없음. 다음 keystroke 까지 ms 단위로 비어
    //    있는 상태 보장. setText("") 후 사용자가 바로 친 글자는 React 의 다음
    //    update 로 들어가며 덮어쓰이지 않음.
    enqueueMessage(trimmed, filesSnapshot);
    setText("");
    setPendingFiles([]);
    if (textareaRef.current) textareaRef.current.value = "";
    stickToBottomRef.current = true;
  }

  // [ERP-TEAMCHAT-ENTER-BEHAVIOR-PC-MOBILE-SPLIT-DEV-20260702-025]
  // PC: Enter 전송(Shift+Enter 줄바꿈, IME 조합 중 전송 금지). 모바일: 기본 줄바꿈만(버튼 전송).
  // 023 에서 제거했던 Enter 전송을 PC 한정으로 복원.
  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (isMobile) return; // 모바일: textarea 기본 동작 유지(Enter 특별처리 안 함), 줄바꿈은 버튼으로 보장
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send(e.currentTarget.value);
    }
  }

  // [ERP-TEAMCHAT-MOBILE-NEWLINE-BUTTON-DEV-20260702-026]
  // 모바일 전용 "줄바꿈" 버튼용 — 현재 커서 위치(selectionStart/End)에 "\n" 삽입 후
  // 커서를 줄바꿈 뒤로 이동. controlled textarea 라 setText 로 반영하고, rAF 로 focus/caret 복원.
  // 높이 재계산은 text 변경 useEffect(resizeInputTextarea) 가 처리.
  function insertNewline() {
    const el = textareaRef.current;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    const next = text.slice(0, start) + "\n" + text.slice(end);
    setText(next);
    const caret = start + 1;
    requestAnimationFrame(() => {
      const t = textareaRef.current;
      if (!t) return;
      t.focus();
      t.setSelectionRange(caret, caret);
    });
  }

  // [ERP-TEAM-CHAT-INPUT-AUTOGROW-5LINES-DEV-20260629-001]
  // 입력창 auto-grow — 내용에 따라 1줄에서 최대 5줄까지 높이 증가, 초과 시 내부 스크롤.
  //   height=auto 로 리셋 후 scrollHeight 로 재계산, max(=5줄) 로 clamp.
  //   line-height 20px(leading-5) × 5 + 세로 padding(widget py-1=8 / page py-2=16).
  const MAX_LINES = 5;
  const LINE_H = 20;
  const resizeInputTextarea = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    const padV = mode === "widget" ? 8 : 16; // py-1 / py-2 합계(px)
    const maxH = LINE_H * MAX_LINES + padV;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, maxH)}px`;
    el.style.overflowY = el.scrollHeight > maxH ? "auto" : "hidden";
  }, [mode]);

  // text 변경(입력/전송 후 "" 리셋 포함) 시마다 높이 재계산.
  useEffect(() => {
    resizeInputTextarea();
  }, [text, resizeInputTextarea]);

  function onDragEnter(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes("Files")) setDragActive(true);
  }
  function onDragOver(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
  }
  function onDragLeave(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }
  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files.length > 0) appendFiles(e.dataTransfer.files);
  }

  const statusLabel = (() => {
    if (mode === "page") {
      // 페이지에서는 WS 표시는 우선 생략 (기존 문구 유지 + 힌트)
      if (realtimeStatus === "polling") return "메시지 " + messages.length + "개 · 폴링 모드";
      if (realtimeStatus === "offline") return "메시지 " + messages.length + "개 · 오프라인";
      return "메시지 " + messages.length + "개 · 실시간";
    }
    if (realtimeStatus === "live") return "실시간";
    if (realtimeStatus === "polling") return "폴링";
    if (realtimeStatus === "offline") return "오프라인";
    return "연결 중…";
  })();

  const isWidget = mode === "widget";

  return (
    <div
      ref={containerRef}
      className={`${
        isWidget ? "flex flex-col h-full" : "flex flex-col h-screen"
      } bg-white`}
    >
      {/* 헤더 */}
      <header
        className={`${
          isWidget
            ? "bg-blue-600 text-white px-3 py-2 flex items-center gap-2 shrink-0"
            : "bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between shrink-0"
        }`}
      >
        {isWidget ? (
          <>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm truncate">💬 전체 팀 채팅방</div>
              <div className="text-[10px] opacity-80">{statusLabel}</div>
            </div>
            {/* [ERP-FLOATING-CHAT-MEMO-POSITION-PER-DEVICE-DEV-20260629-001] 위치 변경 ⚙️ 메뉴 주입 */}
            {headerExtra}
            <button
              type="button"
              onClick={toggleSound}
              className="w-7 h-7 rounded hover:bg-white/20 flex items-center justify-center"
              title={soundEnabled ? "알림음 켜짐 — 클릭하면 끄기" : "알림음 꺼짐 — 클릭하면 켜기"}
              aria-label={soundEnabled ? "알림음 끄기" : "알림음 켜기"}
            >
              {soundEnabled ? "🔔" : "🔕"}
            </button>
            {onExpandFullScreen && (
              <button
                type="button"
                onClick={onExpandFullScreen}
                className="w-7 h-7 rounded hover:bg-white/20 flex items-center justify-center"
                title="전체화면"
                aria-label="전체화면"
              >
                ⛶
              </button>
            )}
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="w-7 h-7 rounded hover:bg-white/20 flex items-center justify-center text-lg"
                title="닫기"
                aria-label="닫기"
              >
                ×
              </button>
            )}
          </>
        ) : (
          <>
            <div>
              <h1 className="text-xl font-bold text-gray-900">💬 전체 팀 채팅방</h1>
              <p className="text-xs text-gray-500 mt-0.5">{statusLabel}</p>
            </div>
            <button
              type="button"
              onClick={toggleSound}
              className={`text-sm px-3 py-1.5 rounded-lg border ${
                soundEnabled
                  ? "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
                  : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
              }`}
              title={soundEnabled ? "알림음 켜짐 — 클릭하면 끄기" : "알림음 꺼짐 — 클릭하면 켜기 (브라우저 정책상 첫 클릭으로 unlock)"}
            >
              {soundEnabled ? "🔔 알림음 켜짐" : "🔕 알림음 꺼짐"}
            </button>
          </>
        )}
      </header>

      {/* 메시지 목록 */}
      <div
        ref={messagesScrollRef}
        onScroll={onScroll}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`flex-1 overflow-y-auto ${
          isWidget ? "px-3 py-2 bg-gray-50" : "px-6 py-4 bg-gray-50"
        } relative ${dragActive ? "bg-blue-50" : ""}`}
      >
        {dragActive && (
          <div className="absolute inset-2 border-4 border-dashed border-blue-400 bg-blue-50/80 rounded-lg flex items-center justify-center pointer-events-none z-10">
            <p className="text-blue-700 font-bold text-sm">파일을 놓아 업로드</p>
          </div>
        )}
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">
            첫 메시지를 남겨보세요 💬
          </div>
        ) : (
          <div
            className={`${
              isWidget ? "space-y-2" : "max-w-4xl mx-auto space-y-3"
            }`}
          >
            {hasMoreOlder && (
              <div className="flex justify-center pb-1">
                <button
                  type="button"
                  onClick={handleLoadOlder}
                  disabled={loadingOlder}
                  className={`${
                    isWidget ? "px-3 py-1 text-xs" : "px-4 py-1.5 text-sm"
                  } rounded-full border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-50 disabled:cursor-wait`}
                >
                  {loadingOlder ? "로딩 중…" : "이전 메시지 더 보기"}
                </button>
              </div>
            )}
            {messages.map((m) => (
              <MessageRow
                key={m.id}
                msg={m}
                isMine={m.sender_name === currentUser}
                compact={isWidget}
                onPreviewImage={setImagePreview}
                onDelete={handleDeleteMessage}
              />
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* 첨부 프리뷰 */}
      {pendingFiles.length > 0 && (
        <div
          className={`border-t border-gray-200 bg-gray-100 ${
            isWidget ? "px-3 py-2" : "px-6 py-2"
          }`}
        >
          <div
            className={`${
              isWidget ? "flex flex-wrap gap-1" : "max-w-4xl mx-auto flex flex-wrap gap-2"
            }`}
          >
            {pendingFiles.map((f, i) => (
              <PendingFileChip
                key={i}
                file={f}
                onRemove={() => removePending(i)}
              />
            ))}
          </div>
        </div>
      )}

      {/* 입력창 */}
      <div
        className={`border-t border-gray-200 bg-white shrink-0 ${
          isWidget ? "px-2 py-2" : "px-6 py-3"
        }`}
      >
        {/* [ERP-TEAMCHAT-MOBILE-NEWLINE-BUTTON-VISIBLE-FIX-DEV-20260702-026B]
            모바일은 CSS 브레이크포인트(md=768px) 기반 2줄 레이아웃 — 1줄: 입력창, 2줄: [줄바꿈][전송].
            JS isMobile 판별과 무관하게 md 미만 viewport 에서 줄바꿈/전송 버튼이 확실히 보임.
            PC(md+)는 기존과 동일한 1줄 [textarea][전송]. */}
        <div className={isWidget ? "" : "max-w-4xl mx-auto"}>
          {/* Row 1: 첨부 + 입력창 (+ PC 전송) */}
          <div className={`flex items-end ${isWidget ? "gap-1" : "gap-2"}`}>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.xlsx,.xls,.pptx,.ppt,.docx,.doc,.txt,.csv,.zip"
              className="hidden"
              onChange={(e) => {
                if (e.target.files) appendFiles(e.target.files);
                e.target.value = "";
              }}
            />
            <input
              ref={imageInputRef}
              type="file"
              multiple
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                if (e.target.files) appendFiles(e.target.files);
                e.target.value = "";
              }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className={`shrink-0 rounded-lg border border-gray-300 hover:bg-gray-100 ${
                isWidget ? "w-8 h-8 text-base" : "w-10 h-10 text-lg"
              }`}
              title="파일 첨부"
            >
              📎
            </button>
            <button
              type="button"
              onClick={() => imageInputRef.current?.click()}
              className={`shrink-0 rounded-lg border border-gray-300 hover:bg-gray-100 ${
                isWidget ? "w-8 h-8 text-base" : "w-10 h-10 text-lg"
              }`}
              title="이미지 첨부"
            >
              📷
            </button>
            <textarea
              ref={textareaRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              placeholder={
                isWidget
                  ? "메시지…"
                  : isMobile
                    ? "메시지를 입력하세요"
                    : "메시지를 입력하세요 (Enter 전송 · Shift+Enter 줄바꿈)"
              }
              // [ERP-TEAM-CHAT-INPUT-AUTOGROW-5LINES-DEV-20260629-001]
              // leading-5(20px) 고정 + resizeInputTextarea 가 height/overflow 를 1~5줄로 제어.
              // max-h className 은 제거(inline height 가 SoT). 전송 후 text="" → useEffect 가 1줄로 리셋.
              className={`flex-1 border border-gray-300 rounded-lg resize-none leading-5 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isWidget ? "px-2 py-1 text-sm" : "px-3 py-2"
              }`}
              style={{ minHeight: isWidget ? "32px" : "40px", overflowY: "hidden" }}
            />
            {/* PC 전송 — 모바일(md 미만)에서는 아래 버튼줄 사용 */}
            <button
              type="button"
              onClick={() => send()}
              disabled={!text.trim() && pendingFiles.length === 0}
              className={`hidden md:inline-flex items-center justify-center shrink-0 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed ${
                isWidget ? "px-3 py-1 text-sm" : "px-4 py-2"
              }`}
            >
              {queueLen > 0 ? `전송중…(${queueLen})` : "전송"}
            </button>
          </div>
          {/* Row 2: 모바일 전용 [줄바꿈][전송] — md 이상에서 숨김 */}
          <div className="md:hidden flex gap-2 mt-2">
            {/* [ERP-MOBILE-TEAMCHAT-INLINE-NEWLINE-ICON-FIX-DEV-20260702-026C]
                UI 통일 — "줄바꿈" 텍스트 → ↵ 아이콘. PC(md+)는 md:hidden 로 계속 미표시. */}
            <button
              type="button"
              onClick={insertNewline}
              aria-label="줄바꿈"
              className="shrink-0 w-12 h-10 flex items-center justify-center rounded-lg border border-gray-300 text-gray-600 text-lg hover:bg-gray-100"
              title="줄바꿈"
            >
              ↵
            </button>
            <button
              type="button"
              onClick={() => send()}
              disabled={!text.trim() && pendingFiles.length === 0}
              className="flex-1 h-10 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {queueLen > 0 ? `전송중…(${queueLen})` : "전송"}
            </button>
          </div>
        </div>
      </div>

      {/* 이미지 확대 모달 */}
      {imagePreview && (
        /* [ERP-GLOBAL-MODAL-OUTSIDE-CLICK-CLOSE-DISABLE-DEV-20260623-001]
           overlay/이미지 클릭 닫기 제거 — 명시 X 버튼으로만 닫힘 */
        <div
          className="fixed inset-0 z-[200] bg-black/80 flex items-center justify-center p-4"
        >
          <button
            type="button"
            onClick={() => setImagePreview(null)}
            aria-label="닫기"
            className="absolute top-4 right-4 text-white/80 hover:text-white text-3xl leading-none"
          >×</button>
          <img
            src={imagePreview.url}
            alt={imagePreview.filename}
            className="max-w-full max-h-full object-contain"
          />
        </div>
      )}
    </div>
  );
}

// ─── 메시지 행 ───────────────────────────────────────────

function MessageRow({
  msg,
  isMine,
  compact,
  onPreviewImage,
  onDelete,
}: {
  msg: ChatMessage;
  isMine: boolean;
  compact: boolean;
  onPreviewImage: (p: ImagePreview) => void;
  // [ERP-TEAMCHAT-READ-RECEIPTS-AND-SOFT-DELETE-DEV-20260703-031]
  onDelete?: (msg: ChatMessage) => void;
}) {
  // 삭제된 메시지는 첨부를 응답에서 내려주지 않으므로 로드 대상 없음.
  const attachments = msg.is_deleted ? [] : (msg.attachments || []);
  const imageMap = useAuthorizedImage(attachments);

  if (msg.is_system) {
    return (
      <div className="text-center text-xs text-gray-400 py-1">{msg.message}</div>
    );
  }

  const initial = (msg.sender_name || "?").slice(0, 1);
  const roleLabel = msg.sender_role
    ? ROLE_LABELS[msg.sender_role] || msg.sender_role
    : null;

  // 직원별 고정 색상 — user_id 우선, fallback sender_name 해시.
  // 강동준/강원준 같은 유사 이름도 user_id 가 다르면 색상이 항상 달라진다.
  const color = getSenderColor(msg.sender_user_id, msg.sender_name);

  const avatarSize = compact ? "w-7 h-7 text-xs" : "w-9 h-9 text-sm";
  const bubbleMax = compact ? "max-w-[80%]" : "max-w-[70%]";
  const imgMax = compact ? "max-w-[200px] max-h-48" : "max-w-xs max-h-64";
  const fileMax = compact ? "max-w-[140px]" : "max-w-[200px]";

  return (
    <div className={`flex gap-2 ${isMine ? "flex-row-reverse" : ""}`}>
      <div
        className={`shrink-0 rounded-full flex items-center justify-center font-bold ${avatarSize} ${color.avatar}`}
      >
        {initial}
      </div>
      <div
        className={`${bubbleMax} ${
          isMine ? "items-end" : "items-start"
        } flex flex-col`}
      >
        <div
          className={`text-[10px] mb-0.5 ${isMine ? "text-right" : ""}`}
        >
          <span className={`font-semibold ${color.name}`}>{msg.sender_name}</span>
          {roleLabel && <span className="ml-1 text-gray-400">· {roleLabel}</span>}
          <span className="ml-1 text-gray-400">· {formatTime(msg.created_at)}</span>
        </div>

        {/* [ERP-TEAMCHAT-READ-RECEIPTS-AND-SOFT-DELETE-DEV-20260703-031]
            삭제된 메시지 — 원문/첨부/읽음/삭제버튼 숨김, 삭제자 이름 문구만 흐린 톤으로. */}
        {msg.is_deleted && (
          <div
            className={`px-3 py-2 rounded-lg text-sm italic ${
              isMine
                ? "bg-gray-200 text-gray-500 rounded-tr-sm"
                : "bg-gray-100 text-gray-400 border border-gray-200 rounded-tl-sm"
            }`}
          >
            {msg.deleted_by_name
              ? `${msg.deleted_by_name}님이 메시지를 삭제하였습니다.`
              : "메시지가 삭제되었습니다."}
          </div>
        )}

        {!msg.is_deleted && msg.message && (
          <div
            className={`px-3 py-2 rounded-lg whitespace-pre-wrap break-words ${
              compact ? "text-sm" : ""
            } ${
              isMine
                ? "bg-blue-500 text-white rounded-tr-sm"
                : `bg-white border border-l-4 border-gray-200 ${color.accent} text-gray-900 rounded-tl-sm`
            }`}
          >
            {msg.message}
          </div>
        )}

        {!msg.is_deleted && attachments.length > 0 && (
          <div
            className={`mt-1 flex flex-col gap-1 ${
              isMine ? "items-end" : "items-start"
            }`}
          >
            {attachments.map((a) => {
              if (a.kind === "image") {
                const entry = imageMap.get(a.id);
                // entry: undefined = 로딩 중, null = 실패, string = 로드 완료
                if (entry === null) {
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => downloadAttachment(a)}
                      className="flex items-center gap-2 px-3 py-2 border border-red-200 rounded-lg bg-red-50 hover:bg-red-100 text-sm text-left"
                      title="이미지를 불러올 수 없습니다 — 클릭하면 다운로드"
                    >
                      <span className="text-base">⚠️</span>
                      <span
                        className={`text-red-700 font-medium truncate ${fileMax}`}
                      >
                        {a.filename}
                      </span>
                      <span className="text-red-500 text-xs">
                        이미지 로드 실패 · 다운로드
                      </span>
                    </button>
                  );
                }
                return (
                  <div
                    key={a.id}
                    className="rounded-lg overflow-hidden border border-gray-200 bg-gray-100 cursor-zoom-in"
                    onClick={() =>
                      entry && onPreviewImage({ url: entry, filename: a.filename })
                    }
                  >
                    {entry ? (
                      <img
                        src={entry}
                        alt={a.filename}
                        className={`${imgMax} object-cover block`}
                      />
                    ) : (
                      <div className="w-32 h-32 flex items-center justify-center text-xs text-gray-400">
                        로드 중…
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => downloadAttachment(a)}
                  className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 text-sm"
                >
                  <span className="text-base">📎</span>
                  <span
                    className={`text-gray-900 font-medium truncate ${fileMax}`}
                  >
                    {a.filename}
                  </span>
                  <span className="text-gray-500 text-xs">
                    {formatSize(a.size_bytes)}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* [ERP-TEAMCHAT-UNREAD-COUNT-KAKAOTALK-STYLE-FIX-DEV-20260703-031B]
            내가 보낸 메시지(삭제 아님)만 — 카카오톡식 미읽음 인원수만 작게 표시(0이면 미표시) + 삭제 버튼. */}
        {isMine && !msg.is_deleted && (
          <div className="mt-0.5 flex items-center gap-2 text-[10px] text-gray-400">
            {(msg.unread_count ?? 0) > 0 && (
              <span
                className="text-amber-500 font-semibold"
                title={`미읽음: ${(msg.unread_user_names ?? []).join(", ")}`}
              >
                {msg.unread_count}
              </span>
            )}
            {onDelete && (
              <button
                type="button"
                onClick={() => onDelete(msg)}
                className="text-gray-400 hover:text-red-500"
                title="이 메시지 삭제"
              >
                삭제
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── 첨부 프리뷰 칩 ─────────────────────────────────────

function PendingFileChip({
  file,
  onRemove,
}: {
  file: File;
  onRemove: () => void;
}) {
  const [thumb, setThumb] = useState<string | null>(null);
  useEffect(() => {
    if (!file.type.startsWith("image/")) return;
    const url = URL.createObjectURL(file);
    setThumb(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  return (
    <div className="flex items-center gap-2 bg-white border border-gray-300 rounded-lg pl-2 pr-1 py-1 text-xs">
      {thumb ? (
        <img src={thumb} alt={file.name} className="w-6 h-6 rounded object-cover" />
      ) : (
        <span className="text-base">📎</span>
      )}
      <span className="text-gray-900 truncate max-w-[120px]">{file.name}</span>
      <span className="text-[10px] text-gray-500">{formatSize(file.size)}</span>
      <button
        type="button"
        onClick={onRemove}
        className="ml-1 w-5 h-5 rounded hover:bg-gray-200 text-gray-500"
        title="제거"
      >
        ✕
      </button>
    </div>
  );
}
